import React, { useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import copy from 'copy-to-clipboard'

const StyledTextareaWrapper = styled.div`
  border: 1px solid #d5dadf;
  border-radius: 4px;
  height: 200px;
  min-height: 72px;
  position: relative;
`

const StyledTextarea = styled.textarea`
  margin: 0;
  padding: 10px 0;
  height: 200px;
  border-radius: 4px;
  outline: none;
  font-family: 'Roboto Mono', monospace;
  font-size: 14px;
  line-height: 1.2;
  &:focus-visible {
    outline: none;
  }
  padding-left: 3.5rem;
  overflow-x: hidden;
  overflow-y: scroll;
  border: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  &::placeholder {
    color: grey;
  }
`

const StyledNumbers = styled.div`
  margin: 0;
  padding: 10px 0;
  height: 200px;
  border-radius: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  resize: none;
  outline: none;
  font-family: 'Roboto Mono', monospace;
  font-size: 14px;
  line-height: 1.2;
  &:focus-visible {
    outline: none;
  }
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  text-align: right;
  box-shadow: none;
  position: absolute;
  color: #cbcbcb;
  border: none;
  background-color: #f7f7f7;
  padding: 10px;
  width: 3rem;
`

const StyledNumber = styled.div`
  color: ${props => (props.active ? '#f7f7f7' : 'grey')};
`

const StyledButtonCopy = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  height: 28px;
  background-color: #f2f2f2;
  color: #7c7c7c;
  padding: 5px 15px;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.03em;
  border-radius: 4px;
  outline: none;
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s linear;
`

export const TextAreaWithClipboard = ({
  value,
  onValueChange,
  clipboardOnClick,
  placeholder,
  btnCopyVisible,
  btnCopyText,
  readonly,
  'data-mu-test': dataAttributeTest
}) => {
  const lineCounterRef = useRef(null)
  const textareaRef = useRef(null)
  const wrapperRef = useRef(null)
  const btnCopyRef = useRef(null)

  // conta le linee
  const lineCount = useMemo(() => value && value.split('\n').length, [value])
  // genera un array per rappresetare i numeri (le linee)
  const linesArr = useMemo(
    () => Array.from({ length: lineCount + 500 }, (e, i) => i + 1),
    [lineCount]
  )

  const handleTextareaChange = event => {
    onValueChange(event.target.value)
  }

  const handleTextareaScroll = () => {
    if (lineCounterRef.current && textareaRef.current) {
      lineCounterRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  const handleResize = e => {
    const { height } = e[0].contentRect
    if (lineCounterRef && lineCounterRef.current) {
      lineCounterRef.current.style.height = height + 20 + 'px'
    }
    if (wrapperRef && wrapperRef.current) {
      wrapperRef.current.style.height = height + 22 + 'px'
    }
  }

  const handleClick = e => {
    copy(value)
    clipboardOnClick(e, value)
  }

  const observer = useRef(new ResizeObserver(handleResize))

  useEffect(() => {
    if (textareaRef && textareaRef.current) {
      observer.current.observe(textareaRef.current)
    }
    return () => {
      observer.current.disconnect()
    }
  }, [textareaRef])

  return (
    <StyledTextareaWrapper
      onMouseEnter={() => {
        if (btnCopyVisible) {
          btnCopyRef.current.style.opacity = 1
        }
      }}
      onMouseLeave={() => {
        if (btnCopyVisible) {
          btnCopyRef.current.style.opacity = 0
        }
      }}
      ref={wrapperRef}
    >
      <StyledNumbers ref={lineCounterRef}>
        {linesArr.map(count => (
          <StyledNumber key={count}>{count}</StyledNumber>
        ))}
      </StyledNumbers>
      <StyledTextarea
        readonly={readonly}
        data-mu-test={dataAttributeTest}
        onChange={handleTextareaChange}
        onScroll={handleTextareaScroll}
        placeholder={placeholder}
        ref={textareaRef}
        value={value}
        wrap='off'
      />
      {btnCopyVisible && (
        <StyledButtonCopy ref={btnCopyRef} onClick={handleClick} kind='neutral'>
          {btnCopyText}
        </StyledButtonCopy>
      )}
    </StyledTextareaWrapper>
  )
}

TextAreaWithClipboard.defaultProps = {
  clipboardOnClick: () => {},
  onValueChange: () => {},
  btnCopyVisible: true,
  btnCopyText: 'COPY',
  readonly: true,
  placeholder: ''
}

TextAreaWithClipboard.propTypes = {
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  placeholder: PropTypes.string,
  clipboardOnClick: PropTypes.func,
  btnCopyVisible: PropTypes.bool,
  btnCopyText: PropTypes.string,
  readonly: PropTypes.bool,
  'data-mu-test': PropTypes.string
}
